/**
 * Read-only projection of open exploration questions for a property, one
 * `ListCardRow` per bullet. Derived from `useProductMap(propertyId)` — every
 * capability's `openQuestionsMd`, walked across `areas[].capabilities` and
 * `unassignedCapabilities` — rather than its own endpoint, since the map
 * already carries the data this card needs. This card never edits
 * `openQuestionsMd` itself (Decision 2026-09-17) — both the card-level
 * "Answer questions" button (all open questions) and each row's "Answer"
 * button (leading with that one) send the assistant to work the questions as
 * leads on a fresh thread, not edit them here.
 *
 * @module features/exploration/components/OpenQuestionsCard
 */

import { Button, ListCard, ListCardRow, Text } from '@chatsdet/ui';
import { useAssistant } from '../../assistant/AssistantContext';
import { openQuestionsPrompt, parseOpenQuestionBullets } from '../../product-map/openQuestions';
import type { OpenQuestionLead } from '../../product-map/openQuestions';
import { useProductMap } from '../../product-map/useProductMap';
import type { ProductCapability } from '../../product-map/types';

interface OpenQuestionRow extends OpenQuestionLead {
  capabilityId: string;
}

function rowsForCapability(capability: ProductCapability): OpenQuestionRow[] {
  return parseOpenQuestionBullets(capability.openQuestionsMd).map((question) => ({
    capabilityId: capability.id,
    code: capability.code,
    title: capability.title,
    question,
  }));
}

export function OpenQuestionsCard({ propertyId }: { propertyId: string }) {
  const { askAssistant } = useAssistant();
  const mapQ = useProductMap(propertyId);
  const map = mapQ.data;

  const questions: OpenQuestionRow[] = map
    ? [
        ...map.areas.flatMap((area) => area.capabilities.flatMap(rowsForCapability)),
        ...map.unassignedCapabilities.flatMap(rowsForCapability),
      ]
    : [];

  if (mapQ.isLoading || mapQ.isError || questions.length === 0) return null;

  return (
    <ListCard
      title="Open questions"
      action={
        <Button variant="primary" size="sm" onPress={() => askAssistant(openQuestionsPrompt(), { propertyId })}>
          Answer questions
        </Button>
      }
    >
      {questions.map((q, index) => (
        <ListCardRow
          key={`${q.capabilityId}-${index}`}
          index={index}
          title={
            <>
              {q.code && <span className="font-mono text-muted-foreground/70">{q.code}</span>} {q.title}
            </>
          }
          meta={<Text size="xs">{q.question}</Text>}
          actions={
            <Button variant="secondary" size="sm" onPress={() => askAssistant(openQuestionsPrompt(q), { propertyId })}>
              Answer
            </Button>
          }
        />
      ))}
    </ListCard>
  );
}
